import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  ChevronUp,
  Download,
  Edit3,
  File,
  Folder,
  FolderPlus,
  RefreshCw,
  Save,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { getResponseErrorMessage } from '../../../utils/apiError';
import { formatBytes } from '../../../utils/customUi';

interface VolumeRoot {
  mountKey: string;
  label: string;
  containerPath: string;
  readOnly: boolean;
  available: boolean;
  availability: 'available' | 'prepared' | 'missing' | 'deleting' | 'deleted';
}

interface VolumeRootsResponse {
  schemaVersion: 1;
  instanceId: string;
  instanceState: string;
  storage: { used: number | null; limit: number };
  volumes: VolumeRoot[];
}

interface FileItem {
  name: string;
  type: 'directory' | 'file';
  size: number | null;
  modified: string;
  path: string;
}

function authHeaders(extra: Record<string, string> = {}): HeadersInit {
  const token = localStorage.getItem('token');
  return { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...extra };
}

function childPath(parent: string, name: string): string {
  return parent === '/' ? `/${name}` : `${parent.replace(/\/$/, '')}/${name}`;
}

function availabilityMessage(root: VolumeRoot, instanceState: string): string {
  if (instanceState === 'deleting') return 'The instance is deleting; persistent data is locked for cleanup.';
  if (instanceState === 'deleted') return 'The instance was deleted and its persistent data is no longer available.';
  if (root.availability === 'prepared') return 'This volume is created on the first successful start.';
  if (root.availability === 'missing') return 'The declared persistent volume is missing on the Agent.';
  return 'Persistent volume is unavailable.';
}

export function CustomPersistentVolumes({ instanceId }: { instanceId: string }) {
  const [roots, setRoots] = useState<VolumeRootsResponse | null>(null);
  const [selectedKey, setSelectedKey] = useState('');
  const [currentPath, setCurrentPath] = useState('/');
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [mutating, setMutating] = useState(false);
  const [error, setError] = useState('');
  const [editor, setEditor] = useState<{ path: string; name: string; content: string } | null>(null);
  const uploadRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(
    () => roots?.volumes.find(volume => volume.mountKey === selectedKey) || roots?.volumes[0] || null,
    [roots, selectedKey],
  );

  const fetchRoots = useCallback(async () => {
    const response = await fetch(`/api/admin/custom-instances/${instanceId}/files/volumes`, {
      headers: authHeaders(),
    });
    if (!response.ok) throw new Error(await getResponseErrorMessage(response, 'Failed to load persistent volumes'));
    const data = await response.json() as VolumeRootsResponse;
    setRoots(data);
    setSelectedKey(current => data.volumes.some(volume => volume.mountKey === current)
      ? current
      : (data.volumes[0]?.mountKey || ''));
    return data;
  }, [instanceId]);

  const fetchFiles = useCallback(async (path: string, rootValue?: VolumeRoot | null) => {
    const root = rootValue || selected;
    if (!root?.available) {
      setFiles([]);
      setCurrentPath('/');
      return;
    }
    const response = await fetch(
      `/api/admin/custom-instances/${instanceId}/files/volumes/${encodeURIComponent(root.mountKey)}`
      + `/list?path=${encodeURIComponent(path)}`,
      { headers: authHeaders() },
    );
    if (!response.ok) throw new Error(await getResponseErrorMessage(response, 'Failed to list persistent volume'));
    const data = await response.json();
    setFiles(Array.isArray(data.files) ? data.files : []);
    setCurrentPath(typeof data.currentPath === 'string' ? data.currentPath : path);
  }, [instanceId, selected]);

  const refresh = useCallback(async (path = currentPath) => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchRoots();
      const root = data.volumes.find(volume => volume.mountKey === selectedKey) || data.volumes[0] || null;
      await fetchFiles(path, root);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [currentPath, fetchFiles, fetchRoots, selectedKey]);

  useEffect(() => { void refresh('/'); }, [instanceId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCurrentPath('/');
    setFiles([]);
    if (selected) void fetchFiles('/', selected).catch(caught => setError(caught instanceof Error ? caught.message : String(caught)));
  }, [selectedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const mutate = async (url: string, init: RequestInit) => {
    setMutating(true);
    setError('');
    try {
      const response = await fetch(url, init);
      if (!response.ok) throw new Error(await getResponseErrorMessage(response, 'Persistent volume mutation failed'));
      const result = await response.json();
      if (result.terminal === false) throw new Error('The mutation outcome is still being resolved; refresh before retrying.');
      await refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setMutating(false);
    }
  };

  const mutationUrl = (suffix: string, path: string) => (
    `/api/admin/custom-instances/${instanceId}/files/volumes/${encodeURIComponent(selected!.mountKey)}`
    + `/${suffix}?path=${encodeURIComponent(path)}`
  );

  const upload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    for (const item of selectedFiles) {
      await mutate(mutationUrl('upload', childPath(currentPath, item.name)), {
        method: 'PUT',
        headers: authHeaders({
          'Content-Type': 'application/octet-stream',
          'Idempotency-Key': crypto.randomUUID(),
        }),
        body: item,
      });
    }
    if (uploadRef.current) uploadRef.current.value = '';
  };

  const createDirectory = async () => {
    const name = window.prompt('Directory name')?.trim();
    if (!name) return;
    await mutate(mutationUrl('directories', childPath(currentPath, name)), {
      method: 'POST',
      headers: authHeaders({ 'Idempotency-Key': crypto.randomUUID() }),
    });
  };

  const remove = async (item: FileItem) => {
    if (!window.confirm(`Delete ${item.name}?`)) return;
    await mutate(mutationUrl('paths', item.path), {
      method: 'DELETE',
      headers: authHeaders({ 'Idempotency-Key': crypto.randomUUID() }),
    });
  };

  const edit = async (item: FileItem) => {
    setError('');
    const response = await fetch(
      mutationUrl('read', item.path),
      { headers: authHeaders() },
    );
    if (!response.ok) {
      setError(await getResponseErrorMessage(response, 'Failed to read file'));
      return;
    }
    const data = await response.json();
    setEditor({ path: item.path, name: item.name, content: String(data.content || '') });
  };

  const save = async () => {
    if (!editor) return;
    const body = new Blob([editor.content], { type: 'text/plain;charset=utf-8' });
    await mutate(mutationUrl('write', editor.path), {
      method: 'PUT',
      headers: authHeaders({
        'Content-Type': 'application/octet-stream',
        'Idempotency-Key': crypto.randomUUID(),
      }),
      body,
    });
    setEditor(null);
  };

  const download = async (item: FileItem) => {
    setError('');
    const response = await fetch(mutationUrl('download', item.path), { headers: authHeaders() });
    if (!response.ok) {
      setError(await getResponseErrorMessage(response, 'Download failed'));
      return;
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = item.name;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const writable = Boolean(selected?.available && !selected.readOnly && !['deleting', 'deleted'].includes(roots?.instanceState || ''));
  const segments = currentPath.split('/').filter(Boolean);

  return <section className="space-y-4" aria-labelledby="custom-files-title">
    <div>
      <h2 id="custom-files-title" className="section-title">Files</h2>
      <p className="section-subtitle mt-1">Persistent data is managed per declared mount; storage usage and quota apply to the whole instance.</p>
    </div>
    <div className="card overflow-hidden">
      <div className="border-b border-dark-700 px-4 pt-3" role="tablist" aria-label="Custom instance file namespaces">
        <button type="button" role="tab" aria-selected="true" className="px-3 py-2 text-sm border-b-2 border-neon-400 text-neon-300">
          Persistent volumes
        </button>
      </div>

      <div className="p-4 border-b border-dark-700 flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Persistent volume roots">
          {roots?.volumes.map(volume => <button
            key={volume.mountKey}
            type="button"
            role="tab"
            aria-selected={volume.mountKey === selected?.mountKey}
            className={volume.mountKey === selected?.mountKey ? 'btn-primary' : 'btn-secondary'}
            onClick={() => setSelectedKey(volume.mountKey)}
          >
            {volume.label}
            <span className={volume.readOnly ? 'badge-warning ml-2' : 'badge-online ml-2'}>{volume.readOnly ? 'read-only' : 'read-write'}</span>
          </button>)}
        </div>
        <div className="ml-auto text-xs text-gray-500">
          Total storage: {roots?.storage.used === null || roots?.storage.used === undefined ? 'unavailable' : formatBytes(roots.storage.used)}
          {roots?.storage.limit ? ` / ${formatBytes(roots.storage.limit)}` : ''}
        </div>
      </div>

      {selected && <div className="px-4 py-3 border-b border-dark-700 bg-dark-900/30 flex flex-wrap items-center gap-3 text-xs">
        <div><span className="text-gray-600">Container path</span><code className="ml-2 text-cyber-400">{selected.containerPath}</code></div>
        <div><span className="text-gray-600">Instance state</span><span className="ml-2 text-gray-300">{roots?.instanceState}</span></div>
        <button type="button" className="btn-ghost p-2 ml-auto" title="Refresh persistent volumes" onClick={() => void refresh()} disabled={loading || mutating}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>}

      {error && <div className="m-4 p-3 bg-red-900/30 border border-red-800 rounded-lg flex items-center gap-2 text-red-300 text-sm">
        <AlertCircle className="w-4 h-4 shrink-0" /><span>{error}</span><button type="button" className="ml-auto" onClick={() => setError('')}><X className="w-4 h-4" /></button>
      </div>}

      {!loading && roots?.volumes.length === 0 && <div className="empty-state m-4"><Folder className="empty-state-icon" /><div className="empty-state-title">No persistent volumes declared</div><p className="empty-state-text">The pinned launch spec has no data mounts.</p></div>}
      {!loading && selected && !selected.available && <div className="empty-state m-4"><Folder className="empty-state-icon" /><div className="empty-state-title">Persistent volume unavailable</div><p className="empty-state-text">{availabilityMessage(selected, roots?.instanceState || '')}</p></div>}

      {selected?.available && <>
        <div className="px-4 py-2 border-b border-dark-700 flex flex-wrap items-center gap-2">
          <button type="button" className="btn-ghost p-2" title="Parent directory" disabled={currentPath === '/' || loading} onClick={() => void fetchFiles(currentPath.split('/').slice(0, -1).join('/') || '/')}><ChevronUp className="w-4 h-4" /></button>
          <button type="button" className="btn-ghost p-2" title="New directory" disabled={!writable || mutating} onClick={() => void createDirectory()}><FolderPlus className="w-4 h-4" /></button>
          <button type="button" className="btn-ghost p-2" title="Upload files" disabled={!writable || mutating} onClick={() => uploadRef.current?.click()}><Upload className="w-4 h-4" /></button>
          <input ref={uploadRef} className="hidden" type="file" multiple onChange={event => void upload(event)} />
          <div className="text-xs text-gray-500 ml-2">/{segments.join('/')}</div>
          {selected.readOnly && <span className="ml-auto text-xs text-amber-300">Mutations are disabled by the pinned spec.</span>}
        </div>

        <div className="overflow-x-auto min-h-40">
          {loading ? <div className="py-12"><div className="spinner w-8 h-8 mx-auto" /></div> : files.length === 0
            ? <div className="py-12 text-center text-sm text-gray-500">This directory is empty.</div>
            : <table className="w-full text-sm"><thead className="text-left text-gray-500 bg-dark-900/20"><tr><th className="px-4 py-2">Name</th><th className="px-4 py-2">Size</th><th className="px-4 py-2">Modified</th><th className="px-4 py-2 text-right">Actions</th></tr></thead><tbody className="divide-y divide-dark-700">{files.map(item => <tr key={item.path}>
              <td className="px-4 py-3"><button type="button" className="flex items-center gap-2 text-gray-200 hover:text-neon-300" onClick={() => item.type === 'directory' && void fetchFiles(item.path)}>{item.type === 'directory' ? <Folder className="w-4 h-4 text-yellow-500" /> : <File className="w-4 h-4 text-gray-500" />}{item.name}</button></td>
              <td className="px-4 py-3 text-gray-500">{item.size === null ? '—' : formatBytes(item.size)}</td>
              <td className="px-4 py-3 text-gray-500">{item.modified ? new Date(item.modified).toLocaleString() : '—'}</td>
              <td className="px-4 py-3"><div className="flex justify-end gap-1">{item.type === 'file' && <><button type="button" className="btn-ghost p-2" title="Download" onClick={() => void download(item)}><Download className="w-4 h-4" /></button><button type="button" className="btn-ghost p-2" title="Edit text" disabled={!writable} onClick={() => void edit(item)}><Edit3 className="w-4 h-4" /></button></>}<button type="button" className="btn-ghost p-2 text-red-400" title="Delete" disabled={!writable || mutating} onClick={() => void remove(item)}><Trash2 className="w-4 h-4" /></button></div></td>
            </tr>)}</tbody></table>}
        </div>
      </>}
    </div>

    {editor && <div className="fixed inset-0 bg-black/75 z-50 p-4 flex items-center justify-center" role="dialog" aria-modal="true" aria-labelledby="custom-file-editor-title">
      <div className="card w-full max-w-4xl h-[75vh] flex flex-col">
        <div className="p-4 border-b border-dark-700 flex items-center gap-3"><Edit3 className="w-5 h-5 text-neon-400" /><h3 id="custom-file-editor-title" className="font-semibold text-gray-200">{editor.name}</h3><button type="button" className="btn-ghost p-2 ml-auto" onClick={() => setEditor(null)}><X className="w-4 h-4" /></button></div>
        <textarea className="flex-1 m-4 p-3 rounded bg-black/40 border border-dark-600 font-mono text-xs text-gray-200 resize-none" value={editor.content} onChange={event => setEditor({ ...editor, content: event.target.value })} />
        <div className="p-4 border-t border-dark-700 flex justify-end gap-2"><button type="button" className="btn-secondary" onClick={() => setEditor(null)}>Cancel</button><button type="button" className="btn-primary" disabled={mutating} onClick={() => void save()}><Save className="w-4 h-4" /> Save</button></div>
      </div>
    </div>}
  </section>;
}
